import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  Search,
  ChevronDown,
  ChevronUp,
  Download,
  User,
  Filter,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Banknote,
  CalendarDays,
  ReceiptText,
  ArrowDownRight,
 
} from 'lucide-react';

// Couleurs KLASYS
const COLORS = {
  primary: '#3084b5',      // Bleu principal
  secondary: '#319190',    // Turquoise
  accent: '#c66d59',       // Terracotta/Corail
};

const HistoriqueEnseignantsModal = ({ show, onClose, loadData, onDownloadFiche, showToast }) => {
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterMois, setFilterMois] = useState('tous');
  const [filterAnnee, setFilterAnnee] = useState('tous');
  const [filterType, setFilterType] = useState('tous');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const monthAbbrev = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

  useEffect(() => {
    if (show) {
      loadHistorique();
    }
  }, [show]);

  const loadHistorique = async () => {
    setLoading(true);
    try {
      const data = await loadData();
      setHistorique(data);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFiche = async (paiementId) => {
    try {
      if (onDownloadFiche) {
        await onDownloadFiche(paiementId);
      } else {
        if (showToast) {
          showToast('Fonction de téléchargement non configurée', 'error');
        }
      }
    } catch (error) {
      console.error('Erreur téléchargement fiche:', error);
      if (showToast) {
        showToast('Erreur lors du téléchargement', 'error');
      }
    }
  };

  const anneesUniques = useMemo(() => 
    [...new Set(historique.map(p => p.annee))].sort((a, b) => b - a), 
    [historique]
  );
  
  const moisUniques = useMemo(() => 
    [...new Set(historique.map(p => p.mois))].sort((a, b) => a - b), 
    [historique]
  );

  const paiementsFiltres = useMemo(() => {
    return historique.filter(paiement => {
      const matchSearch = 
        `${paiement.employe_prenom} ${paiement.employe_nom}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        paiement.mode_paiement_nom?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchStatut = filterStatut === 'tous' || paiement.statut === filterStatut;
      const matchMois = filterMois === 'tous' || paiement.mois === parseInt(filterMois);
      const matchAnnee = filterAnnee === 'tous' || paiement.annee === parseInt(filterAnnee);
      const matchType = filterType === 'tous' || paiement.type_contrat === filterType;
      
      return matchSearch && matchStatut && matchMois && matchAnnee && matchType;
    });
  }, [historique, searchTerm, filterStatut, filterMois, filterAnnee, filterType]);

  const stats = useMemo(() => ({
    total: paiementsFiltres.length,
    totalMontant: paiementsFiltres.reduce((sum, p) => sum + parseFloat(p.montant_net || 0), 0),
    totalBrut: paiementsFiltres.reduce((sum, p) => sum + parseFloat(p.montant_brut || 0), 0),
    totalRetenues: paiementsFiltres.reduce((sum, p) => sum + parseFloat(p.montant_retenues || 0), 0),
    clotures: paiementsFiltres.filter(p => p.statut === 'cloture').length,
    enCours: paiementsFiltres.filter(p => p.statut === 'paye').length,
    horaires: paiementsFiltres.filter(p => p.type_contrat === 'HORAIRE').length,
    fixes: paiementsFiltres.filter(p => p.type_contrat === 'MENSUEL').length
  }), [paiementsFiltres]);

  const getMoisNom = (mois) => monthNames[mois - 1] || mois;
  const getMoisAbbrev = (mois) => monthAbbrev[mois - 1] || mois;

  const hasActiveFilters = searchTerm || filterStatut !== 'tous' || filterMois !== 'tous' || filterAnnee !== 'tous' || filterType !== 'tous';
  const activeFilterCount = [searchTerm, filterStatut !== 'tous', filterMois !== 'tous', filterAnnee !== 'tous', filterType !== 'tous'].filter(Boolean).length;

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatut('tous');
    setFilterMois('tous');
    setFilterAnnee('tous');
    setFilterType('tous');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-slate-900/60" />
      
      {/* Modal Container */}
      <div 
        className="relative w-full max-w-[1400px] max-h-[92vh] bg-white overflow-hidden flex flex-col rounded-2xl"
        style={{ 
          boxShadow: '0 25px 80px -12px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.05)'
        }}
      >
        {/* Header */}
        <header className="flex-shrink-0">
          <div 
            className="px-8 py-6 flex items-start justify-between rounded-t-2xl"
            style={{ 
              background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`
            }}
          >
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl">
                <FileSpreadsheet className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white tracking-tight">
                  Historique des rémunérations
                </h1>
                <p className="text-sm text-white/80 mt-0.5">
                  Corps enseignant · Registre comptable
                </p>
              </div>
            </div>
            
            <button 
              onClick={onClose}
              className="p-2.5 text-white/70 hover:text-white hover:bg-white/10 transition-all rounded-xl"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* KPI Strip */}
          <div className="px-8 py-6 bg-slate-50/50 border-b border-slate-200">
            <div className="grid grid-cols-4 gap-4">
              {/* Total décaissé */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Total décaissé
                  </span>
                  <div 
                    className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${COLORS.secondary}15`, color: COLORS.secondary }}
                  >
                    <TrendingUp className="w-4 h-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">
                  {formatCurrency(stats.totalMontant)}
                </p>
                <p className="text-xs text-slate-500 mt-1">FCFA</p>
              </div>

              {/* Transactions */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Transactions
                  </span>
                  <div 
                    className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${COLORS.primary}15`, color: COLORS.primary }}
                  >
                    <ReceiptText className="w-4 h-4" />
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">{stats.total}</p>
                  <span className="text-xs text-slate-400">opérations</span>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs font-medium" style={{ color: COLORS.secondary }}>{stats.clotures} clôturées</span>
                  <span className="text-xs font-medium" style={{ color: COLORS.accent }}>{stats.enCours} en cours</span>
                </div>
              </div>

              {/* Retenues */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Retenues
                  </span>
                  <div 
                    className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${COLORS.accent}15`, color: COLORS.accent }}
                  >
                    <ArrowDownRight className="w-4 h-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">
                  {formatCurrency(stats.totalRetenues)}
                </p>
                <p className="text-xs text-slate-500 mt-1">FCFA déduits</p>
              </div>

              {/* Répartition */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Répartition
                  </span>
                  <div 
                    className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${COLORS.primary}15`, color: COLORS.primary }}
                  >
                    <User className="w-4 h-4" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xl font-bold text-slate-900 tabular-nums">{stats.horaires}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Horaire</p>
                  </div>
                  <div className="w-px h-10 bg-slate-200" />
                  <div>
                    <p className="text-xl font-bold text-slate-900 tabular-nums">{stats.fixes}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Fixe</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Search & Filters Bar */}
        <div className="flex-shrink-0 px-8 py-4 bg-white border-b border-slate-200">
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, prénom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 text-sm border border-slate-200 bg-slate-50 rounded-xl placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:bg-white transition-all"
                style={{ '--tw-ring-color': `${COLORS.primary}40` }}
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border rounded-xl transition-all ${
                showFilters || hasActiveFilters
                  ? 'text-white border-transparent'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
              }`}
              style={showFilters || hasActiveFilters ? { backgroundColor: COLORS.primary } : {}}
            >
              <Filter className="w-4 h-4" />
              Filtres
              {activeFilterCount > 0 && (
                <span 
                  className={`text-xs px-2 py-0.5 font-semibold rounded-lg ${showFilters ? 'bg-white/20 text-white' : ''}`}
                  style={!showFilters ? { backgroundColor: COLORS.primary, color: 'white' } : {}}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: COLORS.accent }}
              >
                Réinitialiser
              </button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Type de contrat
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 bg-white rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer"
                  style={{ '--tw-ring-color': `${COLORS.primary}40` }}
                >
                  <option value="tous">Tous les types</option>
                  <option value="HORAIRE">Horaire (horaire)</option>
                  <option value="MENSUEL">Fixe (mensuel)</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Statut
                </label>
                <select
                  value={filterStatut}
                  onChange={(e) => setFilterStatut(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 bg-white rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer"
                  style={{ '--tw-ring-color': `${COLORS.primary}40` }}
                >
                  <option value="tous">Tous les statuts</option>
                  <option value="paye">Payé</option>
                  <option value="cloture">Clôturé</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Période
                </label>
                <select
                  value={filterMois}
                  onChange={(e) => setFilterMois(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 bg-white rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer"
                  style={{ '--tw-ring-color': `${COLORS.primary}40` }}
                >
                  <option value="tous">Tous les mois</option>
                  {moisUniques.map(mois => (
                    <option key={mois} value={mois}>{getMoisNom(mois)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Exercice
                </label>
                <select
                  value={filterAnnee}
                  onChange={(e) => setFilterAnnee(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 bg-white rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer"
                  style={{ '--tw-ring-color': `${COLORS.primary}40` }}
                >
                  <option value="tous">Toutes les années</option>
                  {anneesUniques.map(annee => (
                    <option key={annee} value={annee}>{annee}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Table Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div 
                className="w-10 h-10 border-2 border-slate-200 rounded-full animate-spin mb-4"
                style={{ borderTopColor: COLORS.primary }}
              />
              <p className="text-sm text-slate-500">Chargement des données...</p>
            </div>
          ) : paiementsFiltres.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 bg-slate-100 flex items-center justify-center rounded-2xl mb-4">
                <FileSpreadsheet className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">Aucune transaction trouvée</p>
              <p className="text-xs text-slate-500">Modifiez vos critères de recherche</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Bénéficiaire
                  </th>
                  <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Période
                  </th>
                  <th className="px-4 py-3.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Brut
                  </th>
                  <th className="px-4 py-3.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Retenues
                  </th>
                  <th className="px-4 py-3.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Net
                  </th>
                  <th className="px-4 py-3.5 text-center text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Statut
                  </th>
                  <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Date op.
                  </th>
                  <th className="px-4 py-3.5 text-center text-[10px] font-semibold uppercase tracking-widest text-slate-500 w-24">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paiementsFiltres.map((paiement) => {
                  const isHoraire = paiement.type_contrat === 'HORAIRE';
                  const isExpanded = expandedRow === paiement.id;
                  const retenues = parseFloat(paiement.montant_retenues || 0) + parseFloat(paiement.montant_avances || 0);
                  
                  return (
                    <React.Fragment key={paiement.id}>
                      <tr className={`group transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}>
                        {/* Bénéficiaire */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-xl text-white font-bold text-sm"
                              style={{ 
                                background: isHoraire 
                                  ? `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accent}dd 100%)`
                                  : `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`
                              }}
                            >
                               <User className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-900">
                                {paiement.employe_nom} {paiement.employe_prenom}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                {isHoraire ? 'Horaire' : 'Fixe'}
                                {isHoraire && paiement.nombre_cours && (
                                  <span className="text-slate-400"> · {paiement.nombre_cours} cours</span>
                                )}
                                {!isHoraire && paiement.jours_pointes !== undefined && (
                                  <span className="text-slate-400"> · {paiement.jours_pointes}/{paiement.jours_ouvres}j</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Période */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-sm text-slate-700 font-medium tabular-nums">
                              {getMoisAbbrev(paiement.mois)} {paiement.annee}
                            </span>
                          </div>
                        </td>

                        {/* Brut */}
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm text-slate-600 tabular-nums">
                            {formatCurrency(paiement.montant_brut || 0)}
                          </span>
                        </td>

                        {/* Retenues */}
                        <td className="px-4 py-4 text-right">
                          {retenues > 0 ? (
                            <span className="text-sm tabular-nums" style={{ color: COLORS.accent }}>
                              -{formatCurrency(retenues)}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-300">—</span>
                          )}
                        </td>

                        {/* Net */}
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm font-bold text-slate-900 tabular-nums">
                            {formatCurrency(paiement.montant_net || 0)}
                          </span>
                        </td>

                        {/* Statut */}
                        <td className="px-4 py-4">
                          <div className="flex justify-center">
                            {paiement.statut === 'cloture' ? (
                              <span 
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg"
                                style={{ backgroundColor: `${COLORS.secondary}15`, color: COLORS.secondary }}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Clôturé
                              </span>
                            ) : (
                              <span 
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg"
                                style={{ backgroundColor: `${COLORS.accent}15`, color: COLORS.accent }}
                              >
                                <Clock className="w-3.5 h-3.5" />
                                En cours
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-4">
                          <p className="text-sm text-slate-700 tabular-nums">
                            {new Date(paiement.date_paiement).toLocaleDateString('fr-FR', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: '2-digit' 
                            })}
                          </p>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setExpandedRow(isExpanded ? null : paiement.id)}
                              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all rounded-lg"
                              title="Voir les détails"
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDownloadFiche(paiement.id)}
                              className="p-2 text-slate-400 hover:text-white transition-all rounded-lg"
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.primary}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              title="Télécharger le bulletin"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Details - GRILLE TABLEAU */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="8" className="px-6 py-6 bg-gradient-to-br from-slate-50 to-slate-100/50">
                            <div className="grid grid-cols-3 gap-6">
                              
                              {/* Section 1: Détail Rémunération */}
                              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                <div 
                                  className="px-4 py-3 border-b border-slate-100 flex items-center gap-2"
                                  style={{ backgroundColor: `${COLORS.secondary}10` }}
                                >
                                  <Banknote className="w-4 h-4" style={{ color: COLORS.secondary }} />
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                    Détail rémunération
                                  </h4>
                                </div>
                                <table className="w-full text-sm">
                                  <tbody>
                                    <tr className="border-b border-slate-100">
                                      <td className="px-4 py-3 text-slate-600">Montant brut</td>
                                      <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                                        {formatCurrency(paiement.montant_brut || 0)} FCFA
                                      </td>
                                    </tr>
                                    {parseFloat(paiement.montant_retenues || 0) > 0 && (
                                      <tr className="border-b border-slate-100 bg-red-50/30">
                                        <td className="px-4 py-3 text-slate-600">Retenues</td>
                                        <td className="px-4 py-3 text-right font-semibold tabular-nums" style={{ color: COLORS.accent }}>
                                          -{formatCurrency(paiement.montant_retenues)} FCFA
                                        </td>
                                      </tr>
                                    )}
                                    {parseFloat(paiement.montant_avances || 0) > 0 && (
                                      <tr className="border-b border-slate-100 bg-orange-50/30">
                                        <td className="px-4 py-3 text-slate-600">Avances récupérées</td>
                                        <td className="px-4 py-3 text-right font-semibold text-orange-600 tabular-nums">
                                          -{formatCurrency(paiement.montant_avances)} FCFA
                                        </td>
                                      </tr>
                                    )}
                                    <tr style={{ backgroundColor: `${COLORS.secondary}10` }}>
                                      <td className="px-4 py-4 font-bold text-slate-900">Net à payer</td>
                                      <td className="px-4 py-4 text-right text-lg font-bold tabular-nums" style={{ color: COLORS.secondary }}>
                                        {formatCurrency(paiement.montant_net || 0)} FCFA
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              {/* Section 2: Éléments de calcul */}
                              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                <div 
                                  className="px-4 py-3 border-b border-slate-100 flex items-center gap-2"
                                  style={{ backgroundColor: `${COLORS.primary}10` }}
                                >
                                  <FileSpreadsheet className="w-4 h-4" style={{ color: COLORS.primary }} />
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                    Éléments de calcul
                                  </h4>
                                </div>
                                <table className="w-full text-sm">
                                  <tbody>
                                    <tr className="border-b border-slate-100">
                                      <td className="px-4 py-3 text-slate-600">Type de contrat</td>
                                      <td className="px-4 py-3 text-right">
                                        <span 
                                          className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                                          style={{ 
                                            backgroundColor: isHoraire ? `${COLORS.accent}15` : `${COLORS.primary}15`,
                                            color: isHoraire ? COLORS.accent : COLORS.primary
                                          }}
                                        >
                                          {isHoraire ? 'Horaire' : 'Fixe'}
                                        </span>
                                      </td>
                                    </tr>
                                    {isHoraire ? (
                                      <>
                                        <tr className="border-b border-slate-100">
                                          <td className="px-4 py-3 text-slate-600">Nombre de cours</td>
                                          <td className="px-4 py-3 text-right font-semibold text-slate-900">{paiement.nombre_cours || 0}</td>
                                        </tr>
                                        <tr className="border-b border-slate-100">
                                          <td className="px-4 py-3 text-slate-600">Heures totales</td>
                                          <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                            {parseFloat(paiement.total_heures || 0).toFixed(2)}h
                                          </td>
                                        </tr>
                                      </>
                                    ) : (
                                      <>
                                        <tr className="border-b border-slate-100">
                                          <td className="px-4 py-3 text-slate-600">Jours ouvrés</td>
                                          <td className="px-4 py-3 text-right font-semibold text-slate-900">{paiement.jours_ouvres || 0}</td>
                                        </tr>
                                        <tr className="border-b border-slate-100">
                                          <td className="px-4 py-3 text-slate-600">Jours pointés</td>
                                          <td className="px-4 py-3 text-right font-semibold text-slate-900">{paiement.jours_pointes || 0}</td>
                                        </tr>
                                        {paiement.jours_manquants > 0 && (
                                          <tr className="border-b border-slate-100 bg-red-50/30">
                                            <td className="px-4 py-3 text-slate-600">Jours manquants</td>
                                            <td className="px-4 py-3 text-right font-semibold" style={{ color: COLORS.accent }}>
                                              {paiement.jours_manquants}
                                            </td>
                                          </tr>
                                        )}
                                      </>
                                    )}
                                    <tr>
                                      <td className="px-4 py-3 text-slate-600">Mode de règlement</td>
                                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                        {paiement.mode_paiement_nom || 'Non spécifié'}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              {/* Section 3: Traçabilité */}
                              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                <div 
                                  className="px-4 py-3 border-b border-slate-100 flex items-center gap-2"
                                  style={{ backgroundColor: `${COLORS.accent}10` }}
                                >
                                  <CheckCircle2 className="w-4 h-4" style={{ color: COLORS.accent }} />
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                    Traçabilité
                                  </h4>
                                </div>
                                <table className="w-full text-sm">
                                  <tbody>
                                    <tr className="border-b border-slate-100">
                                      <td className="px-4 py-3 text-slate-600">Validé par</td>
                                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                        {paiement.validateur_prenom} {paiement.validateur_nom}
                                      </td>
                                    </tr>
                                    <tr className="border-b border-slate-100">
                                      <td className="px-4 py-3 text-slate-600">Date paiement</td>
                                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                        {new Date(paiement.date_paiement).toLocaleDateString('fr-FR', {
                                          day: 'numeric',
                                          month: 'short',
                                          year: 'numeric'
                                        })}
                                      </td>
                                    </tr>
                                    {paiement.date_cloture && (
                                      <>
                                        <tr className="border-b border-slate-100" style={{ backgroundColor: `${COLORS.secondary}08` }}>
                                          <td className="px-4 py-3 text-slate-600">Date clôture</td>
                                          <td className="px-4 py-3 text-right font-semibold" style={{ color: COLORS.secondary }}>
                                            {new Date(paiement.date_cloture).toLocaleDateString('fr-FR', {
                                              day: 'numeric',
                                              month: 'short',
                                              year: 'numeric'
                                            })}
                                          </td>
                                        </tr>
                                        {paiement.cloture_prenom && (
                                          <tr className="border-b border-slate-100">
                                            <td className="px-4 py-3 text-slate-600">Clôturé par</td>
                                            <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                              {paiement.cloture_prenom} {paiement.cloture_nom}
                                            </td>
                                          </tr>
                                        )}
                                      </>
                                    )}
                                  </tbody>
                                </table>

                                {/* Notes si présentes */}
                                {(paiement.remarques || paiement.justification_pointages_manquants) && (
                                  <div className="px-4 py-3 border-t border-slate-100" style={{ backgroundColor: `${COLORS.accent}08` }}>
                                    <div className="flex items-start gap-2">
                                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: COLORS.accent }} />
                                      <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: COLORS.accent }}>
                                          Note
                                        </p>
                                        <p className="text-xs text-slate-700 leading-relaxed">
                                          {paiement.remarques || paiement.justification_pointages_manquants}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Bouton téléchargement */}
                            <div className="flex justify-end mt-5 pt-5 border-t border-slate-200">
                              <button
                                onClick={() => handleDownloadFiche(paiement.id)}
                                className="flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-all hover:shadow-lg"
                                style={{ 
                                  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`
                                }}
                              >
                                <Download className="w-4 h-4" />
                                Télécharger le bulletin de paie
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <footer className="flex-shrink-0 px-8 py-4 bg-slate-50 border-t border-slate-200 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{paiementsFiltres.length}</span> transaction{paiementsFiltres.length > 1 ? 's' : ''} affichée{paiementsFiltres.length > 1 ? 's' : ''}
              {hasActiveFilters && <span style={{ color: COLORS.primary }}> · Filtres actifs</span>}
            </p>
        
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HistoriqueEnseignantsModal;